// Per-lift "Generate PPM schedule" action on the Lifts register. Admin/office (D57, Session ARA:
// the /api/scheduling/generate-ppm endpoint is admin/office -- PPM generation is operational, not
// financial): it generates the forward 12-month PPM service jobs for THIS lift (POST with the
// asset id) and shows the result inline below the button. The office-wide generation lives on the
// Scheduling page; this is the single-lift top-up.
import { useState } from "react";
import { api } from "../../../lib/api";
import type { Asset } from "../../../lib/resources";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { useAuth } from "../../../lib/auth";

type Result = { tone: "ok" | "neutral" | "error"; text: string };

export function GeneratePpmButton({ asset, onChanged }: { asset: Asset; onChanged?: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  // The endpoint is admin/office (D57), so anyone else gets a clear note rather than a 403.
  if (user?.role !== "admin" && user?.role !== "office") {
    return <span className="text-xs text-ele-body opacity-60">Admin or office only</span>;
  }

  async function run() {
    setBusy(true);
    setResult(null);
    try {
      const res = await api.scheduling.generatePpm(asset.id);
      setResult(
        res.created > 0
          ? { tone: "ok", text: `Generated ${res.created} PPM visit${res.created === 1 ? "" : "s"} for this lift.` }
          : { tone: "neutral", text: "Schedule is current. No new visits needed." },
      );
      if (res.created > 0) onChanged?.();
    } catch (e) {
      setResult({ tone: "error", text: friendlyApiError(e, "Could not generate the PPM schedule. Please try again.") });
    } finally {
      setBusy(false);
    }
  }

  const toneClass = result?.tone === "ok" ? "text-ele-green" : result?.tone === "error" ? "text-ele-danger" : "text-ele-body";
  return (
    <div>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        // Clear visible label ("Generate PPM", a PPM schedule -- not a LOLER record). The fuller
        // action is also the accessible name (aria-label) and the hover tooltip (title).
        aria-label="Generate PPM schedule"
        title="Generate the PPM service schedule for this lift"
        className="whitespace-nowrap rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:border-ele-green disabled:opacity-50"
      >
        {busy ? "Generating..." : "Generate PPM"}
      </button>
      {result && (
        <p role="status" className={`mt-1 text-xs ${toneClass}`}>
          {result.text}
        </p>
      )}
    </div>
  );
}
